import * as types from '../mutation-types';
import { Module } from 'vuex';
import { SET_PREVIEW_TEMPLATE } from '../mutation-types';

interface State {
  isLoading: boolean;
  isScrolling: boolean;
  selectedMenu: string;
  isMainNavOpen: boolean;
  isStudioMobileProfileMenuOpen: boolean;
  isDirty: boolean;
  formSaveFun: Promise<void> | null;
  studioUrl: string | null; // for custom domains, we store the studioUrl in the store when router is loaded
  affiliateCode: string | null;
  showRefreshModal: boolean;
  backgroundImage: MediaResponse | null;
  showBioUpgrade: boolean;
  showLoginModal: boolean;
  isPWA: boolean;
  showInstallPWA: boolean;
  showCookieNotice: boolean;
  previewTemplate: string | null;
}
const globalModule: Module<State, any> = {
  namespaced: true,
  state: {
    isLoading: false,
    isScrolling: false,
    selectedMenu: 'Dashboard',
    isMainNavOpen: false,
    isStudioMobileProfileMenuOpen: false,
    isDirty: false,
    formSaveFun: null,
    studioUrl: null,
    affiliateCode: null,
    showRefreshModal: false,
    backgroundImage: null,
    showBioUpgrade: true,
    showLoginModal: false,
    isPWA: false,
    showInstallPWA: true,
    showCookieNotice: true,
    previewTemplate: null,
  },
  getters: {
    isLoading: state => state.isLoading,
    showBioUpgrade: state => state.showBioUpgrade,
    showLoginModal: state => state.showLoginModal,
    isScrolling: state => state.isScrolling,
    selectedMenu: state => state.selectedMenu,
    isDirty: state => state.isDirty,
    formSaveFun: state => state.formSaveFun,
    studioUrl: state => state.studioUrl,
    affiliateCode: state => state.affiliateCode,
    showRefreshModal: state => state.showRefreshModal,
    previewTemplate: state => state.previewTemplate,
    backgroundImage(state, getters, rootState, rootGetters) {
      const teacherBio = rootGetters['studio/marvelousBio'];
      return teacherBio?.image;
    },
    isPWA: state => state.isPWA,
    showInstallPWA: state => state.showInstallPWA,
    showCookieNotice: state => state.showCookieNotice,
    showUserbackInStudio(state) {
      if (process.env.VUE_APP_USERBACK_DISPLAY_IN_STUDIO !== 'true')
        return false;
      return !state.isPWA;
    },
  },
  actions: {
    setShowRefreshModal({ commit }, show: boolean) {
      commit(types.SET_SHOW_REFRESH_MODAL, show);
    },
    setShowLoginModal({ commit }, show: boolean) {
      commit(types.SET_SHOW_LOGIN_MODAL, show);
    },
    setLoading({ commit }, isLoading: boolean) {
      commit(types.SET_LOADING_STATE, isLoading);
    },
    setIsScrolling({ commit }, isScrolling: boolean) {
      commit(types.SET_SCROLLING_STATE, isScrolling);
    },
    setSelectedMenu({ commit }, selectedMenu: string) {
      commit(types.SET_SELECTED_MENU, selectedMenu);
    },
    resetState({ dispatch }) {
      dispatch('user/resetState', null, { root: true });
      dispatch('studio/resetState', null, { root: true });
      dispatch('product/resetState', null, { root: true });
      dispatch('setting/resetState', null, { root: true });
    },
    openMainNav({ commit }, isMainNavOpen = true) {
      commit(types.SET_IS_MAIN_NAV_OPEN, isMainNavOpen);
    },
    openStudioMobileProfileMenu(
      { commit },
      isStudioMobileProfileMenuOpen = true
    ) {
      commit(
        types.SET_IS_STUDIO_MOBILE_PROFILE_MENU_OPEN,
        isStudioMobileProfileMenuOpen
      );
    },
    setDirty({ commit }, isDirty) {
      commit(types.SET_IS_DIRTY, isDirty);
    },
    setFormSaveFun({ commit }, fun) {
      commit(types.SET_FORM_SAVE_FUN, fun);
    },
    setStudioUrl({ commit }, studioUrl) {
      commit(types.SET_STUDIO_URL, studioUrl);
    },
    setAffiliateCode({ commit }, affiliateCode) {
      commit(types.SET_AFFILIATE_CODE, affiliateCode);
    },
    setShowBioUpgrade({ commit }, show) {
      commit(types.SET_SHOW_BIO_UPGRADE, show);
    },
    setIsPWA({ commit }, isPWA) {
      commit(types.SET_IS_PWA, isPWA);
    },
    setShowInstallPWA({ commit }, show) {
      commit(types.SET_SHOW_INSTALL_PWA, show);
    },
    setShowCookieNotice({ commit }, show) {
      commit(types.SET_SHOW_COOKIE_NOTICE, show);
    },
    setPreviewTemplate({ commit }, previewTemplate) {
      commit(types.SET_PREVIEW_TEMPLATE, previewTemplate);
    },
  },
  mutations: {
    [types.SET_LOADING_STATE](state: State, isLoading: boolean) {
      state.isLoading = isLoading;
    },
    [types.SET_SCROLLING_STATE](state: State, isScrolling: boolean) {
      state.isScrolling = isScrolling;
    },
    [types.SET_SELECTED_MENU](state: State, selectedMenu: string) {
      state.selectedMenu = selectedMenu;
    },
    [types.SET_IS_MAIN_NAV_OPEN](state: State, isMainNavOpen: boolean) {
      state.isMainNavOpen = isMainNavOpen;
    },
    [types.SET_IS_STUDIO_MOBILE_PROFILE_MENU_OPEN](
      state: State,
      isStudioMobileProfileMenuOpen: boolean
    ) {
      state.isStudioMobileProfileMenuOpen = isStudioMobileProfileMenuOpen;
    },
    [types.SET_IS_DIRTY](state: State, isDirty: boolean) {
      state.isDirty = isDirty;
    },
    [types.SET_FORM_SAVE_FUN](state: State, saveFun) {
      state.formSaveFun = saveFun;
    },
    [types.SET_STUDIO_URL](state: State, studioUrl: string) {
      state.studioUrl = studioUrl;
    },
    [types.SET_AFFILIATE_CODE](state: State, affiliateCode: string) {
      state.affiliateCode = affiliateCode;
    },
    [types.SET_SHOW_BIO_UPGRADE](state: State, show: boolean) {
      state.showBioUpgrade = show;
    },
    [types.SET_SHOW_REFRESH_MODAL](state: State, show: boolean) {
      state.showRefreshModal = show;
    },
    [types.SET_SHOW_LOGIN_MODAL](state: State, show: boolean) {
      state.showLoginModal = show;
    },
    [types.SET_IS_PWA](state: State, isPWA: boolean) {
      state.isPWA = isPWA;
    },
    [types.SET_SHOW_INSTALL_PWA](state: State, show: boolean) {
      state.showInstallPWA = show;
    },
    [types.SET_SHOW_COOKIE_NOTICE](state: State, show: boolean) {
      state.showCookieNotice = show;
    },
    [types.SET_PREVIEW_TEMPLATE](state: State, previewTemplate: string) {
      state.previewTemplate = previewTemplate;
    },
  },
};
export default globalModule;
